<template>
<div class="zug-sortieren">

    <!-- Ablage -->
    <h2>Dein Spruch</h2>

    <p v-if="ablage.length === 0" class="leer">
        Tippe unten einen Baustein an, um ihn hier anzuhängen.
    </p>

    <TransitionGroup v-else tag="ol" name="baustein" class="ablage" :class="rueckmeldung && (rueckmeldung.sitzt ? 'stimmt' : 'daneben')">
        <li v-for="(teil, i) in ablage" :key="teil.id">
            <span class="nr">{{ i + 1 }}</span>
            <span class="text">{{ teil.text }}</span>
            <span class="knoepfe">
                <button @click="hoch(i)" :disabled="i === 0">hoch</button>
                <button @click="runter(i)" :disabled="i === ablage.length - 1">runter</button>
                <button @click="entfernen(teil)">entfernen</button>
            </span>
        </li>
    </TransitionGroup>

    <!-- Vorrat -->
    <h2>Bausteine</h2>

    <p v-if="vorrat.length === 0" class="hinweis">
        Alle Bausteine sind gelegt.
    </p>

    <TransitionGroup v-else tag="div" name="baustein" class="vorrat">
        <button v-for="teil in vorrat" :key="teil.id" @click="waehlen(teil)">
            {{ teil.text }}
        </button>
    </TransitionGroup>

    <!-- Rueckmeldung -->
    <div v-if="rueckmeldung" class="rueckmeldung" :class="rueckmeldung.sitzt ? 'stimmt' : 'daneben'">
        <template v-if="rueckmeldung.sitzt">
            <p class="satz">Das sitzt.</p>
            <p class="spruch">{{ rueckmeldung.spruch }}</p>
        </template>
        <template v-else>
            <p class="satz">Das stimmt noch nicht.</p>
            <ul>
                <li v-for="regel in rueckmeldung.regeln" :key="regel">{{ regel }}</li>
            </ul>
        </template>
    </div>

    <!-- Knopfleiste. Rechts kommt, was dem Container gehoert
         (Neue Uebung / Weiter / Ueberspringen). -->
    <div class="knopfleiste">
        <button class="haupt" @click="$emit('pruefen', ablage)" :disabled="leerSperren && ablage.length === 0">Prüfen</button>
        <button @click="reset()">Zurücksetzen</button>
        <slot name="rechts" />
    </div>

</div>
</template>

<script>
import { ref } from 'vue';
import { mische } from '@/logic/Uebung';

// Ablage und Vorrat EINES Zuges. Prueft nicht selbst: "Pruefen" meldet die
// Ablage nach oben, die Rueckmeldung kommt als Prop zurueck. Jede Aenderung
// an der Ablage meldet "geaendert", damit oben die Rueckmeldung vergeht.
//
// Ein neuer Zug = eine neue Instanz (der Elternteil setzt :key). Darum
// beobachtet hier nichts den Prop "zug".
export default {
    props: {
        zug: {type: Object, required: true},
        mitAblenkern: {type: Boolean, default: false},
        rueckmeldung: {type: Object, default: null},
        // Sitzung: Pruefen mit leerer Ablage zaehlt nicht als Versuch.
        leerSperren: {type: Boolean, default: false},
        // Optional { ablage, vorrat }: so liegt es zu Beginn und nach
        // "Zuruecksetzen" ("Fehler korrigieren"). Ohne Vorgabe ist die
        // Ablage leer und alles liegt im Vorrat.
        vorgabe: {type: Object, default: null}
    },
    emits: ['pruefen', 'geaendert'],
    setup(props, { emit }) {
        const vorratBauen = () => props.vorgabe
            ? [...props.vorgabe.vorrat]
            : mische(props.mitAblenkern
                ? [...props.zug.teile, ...props.zug.ablenker]
                : props.zug.teile)
        const ablageBauen = () => props.vorgabe ? [...props.vorgabe.ablage] : []

        const vorrat = ref(vorratBauen())
        const ablage = ref(ablageBauen())

        const reset = () => {
            ablage.value = ablageBauen()
            vorrat.value = vorratBauen()
            emit('geaendert')
        }

        const waehlen = (teil) => {
            vorrat.value = vorrat.value.filter(t => t !== teil)
            ablage.value.push(teil)
            emit('geaendert')
        }

        const entfernen = (teil) => {
            ablage.value = ablage.value.filter(t => t !== teil)
            vorrat.value.push(teil)
            emit('geaendert')
        }

        // hoch/runter arbeiten ueber die Position, nicht ueber den Baustein —
        // "tausche mit dem Nachbarn" ist nur als Position zu formulieren.
        // Die Wachen sind doppelt gemoppelt zu :disabled, aber :disabled
        // gilt nur fuer den Knopf, die Wache fuer jeden spaeteren Aufrufer.
        const hoch = (i) => {
            if (i === 0) return
            const a = ablage.value
            ;[a[i - 1], a[i]] = [a[i], a[i - 1]]
            emit('geaendert')
        }

        const runter = (i) => {
            if (i === ablage.value.length - 1) return
            const a = ablage.value
            ;[a[i], a[i + 1]] = [a[i + 1], a[i]]
            emit('geaendert')
        }

        return { vorrat, ablage, reset, waehlen, entfernen, hoch, runter }
    }
}
</script>

<style scoped>
/* Abschnittstitel schmal in Versalien, eine Haarlinie bis zum Rand. */
h2 {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin: 0 0 0.6rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

h2::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--zs-rahmen);
}

.hinweis {
    margin: 0 0 1.75rem;
    font-size: 0.95rem;
    color: var(--zs-grau);
}

/* ─── Ablage ─────────────────────────────────────────────────
   Eine Tafel, eine Zeile pro Baustein. Der Rahmen ist immer da und
   faerbt sich nur beim Pruefen, so springt nichts. */

.ablage {
    margin: 0 0 1.75rem;
    padding: 0;
    list-style: none;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.ablage.stimmt {
    border-color: var(--zs-oliv);
    box-shadow: 0 0 0 1px var(--zs-oliv);
}

.ablage.daneben {
    border-color: var(--zs-warnrot);
    box-shadow: 0 0 0 1px var(--zs-warnrot);
}

.ablage li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem 0.75rem;
    padding: 0.5rem 0.75rem;
}

.ablage li + li {
    border-top: 1px solid var(--zs-rahmen);
}

.nr {
    flex: none;
    width: 1.5rem;
    font-family: var(--zs-funkschrift);
    font-size: 0.85rem;
    font-variant-numeric: tabular-nums;
    color: var(--zs-grau);
}

/* Nimmt die Restbreite; min-width:0 erlaubt den Umbruch der Knoepfe
   darunter, sobald es eng wird. */
.text {
    flex: 1 1 12rem;
    min-width: 0;
    line-height: 1.4;
}

.knoepfe {
    flex: none;
    display: flex;
    gap: 0.25rem;
    margin-left: auto;
}

.knoepfe button {
    min-height: 2rem;
    padding: 0.2rem 0.55rem;
    font: inherit;
    font-size: 0.85rem;
    color: var(--zs-grau);
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.knoepfe button:hover:not(:disabled) {
    color: var(--zs-tinte);
    background: var(--zs-flaeche);
    border-color: var(--zs-rahmen);
}

.knoepfe button:disabled {
    opacity: 0.35;
    cursor: default;
}

/* ─── Vorrat ─────────────────────────────────────────────── */

.vorrat {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.75rem;
}

.vorrat button {
    min-height: 2.5rem;
    padding: 0.5rem 0.85rem;
    font: inherit;
    line-height: 1.4;
    color: var(--zs-tinte);
    /* Quelle hellgrau, Ziel (Ablage) weiss: man sieht, was wohin geht. */
    background: var(--zs-flaeche);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
    transition: border-color 0.15s, background-color 0.15s;
}

.vorrat button:hover {
    background: var(--zs-tafel);
    border-color: var(--zs-tinte);
}

/* ─── Rueckmeldung ───────────────────────────────────────────
   Nie nur Farbe: vor dem Satz steht ein Haken oder ein Kreuz. */

.rueckmeldung {
    margin: 0 0 1.75rem;
    padding: 0.85rem 1rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.rueckmeldung.stimmt {
    background: var(--zs-oliv-hell);
    border-color: rgba(85, 107, 47, 0.45);
}

.rueckmeldung.daneben {
    background: var(--zs-warnrot-hell);
    border-color: rgba(179, 38, 30, 0.4);
}

.satz {
    margin: 0;
    font-weight: 650;
}

.stimmt .satz {
    color: var(--zs-oliv);
}

.daneben .satz {
    color: var(--zs-warnrot);
}

.stimmt .satz::before {
    content: '\2713\00a0\00a0';
}

.daneben .satz::before {
    content: '\2715\00a0\00a0';
}

.rueckmeldung ul {
    margin: 0.5rem 0 0;
    padding-left: 1.6rem;
}

.rueckmeldung li {
    margin-bottom: 0.2rem;
    line-height: 1.45;
    color: var(--zs-tinte);
}

.spruch {
    margin: 0.5rem 0 0;
    line-height: 1.45;
}

/* ─── Knopfleiste ────────────────────────────────────────────
   Rechts kommt, was dem Container gehoert (Neue Uebung / Weiter). */

.knopfleiste {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--zs-rahmen);
}

.knopfleiste button {
    min-height: 2.5rem;
    padding: 0.45rem 1rem;
    font: inherit;
    font-weight: 600;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.knopfleiste button:hover:not(:disabled) {
    border-color: var(--zs-tinte);
}

/* Die eigentliche Handlung der Uebung (Pruefen / Senden). */
.knopfleiste .haupt {
    color: #ffffff;
    background: var(--zs-blau);
    border-color: var(--zs-blau);
}

.knopfleiste .haupt:hover:not(:disabled) {
    background: var(--zs-blau-dunkel);
    border-color: var(--zs-blau-dunkel);
}

.knopfleiste button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Funktext in fester Laufweite: Protokolltext, nicht Bedientext. */
.ablage .text,
.vorrat button,
.rueckmeldung .spruch {
    font-family: var(--zs-funkschrift);
}

@media (prefers-reduced-motion: reduce) {
    .vorrat button {
        transition: none;
    }
}
/* ─── Bewegung zeigt, was sich geaendert hat ─────────────────
   Neue Bausteine gleiten kurz herein, umgestellte schieben an ihren
   Platz. Entfernte verschwinden sofort (kein Warten aufs Ausblenden). */
.baustein-enter-active {
    transition: opacity 0.2s ease-out, transform 0.2s ease-out;
}

.baustein-enter-from {
    opacity: 0;
    transform: translateY(6px);
}

.baustein-move {
    transition: transform 0.2s ease-out;
}

/* Leere Ablage: gestrichelter Zielbereich, damit man sieht, wohin die
   Bausteine kommen. */
.leer {
    display: flex;
    align-items: center;
    min-height: 3.25rem;
    margin: 0 0 1.75rem;
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    color: var(--zs-grau);
    background: rgba(255, 255, 255, 0.55);
    border: 1px dashed #9AA3B2;
    border-radius: var(--zs-ecke);
    box-sizing: border-box;
}

@media (prefers-reduced-motion: reduce) {
    .baustein-enter-active,
    .baustein-move {
        transition: none;
    }
}
</style>
